// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { headerStyles } from "@awc/Overrides/Pages/Header";
import { DivWrapper, HEADER_BREAKPOINT } from "@awc/XDL/constants";
import { color, companyLogoItem, layoutContainer, xScreen } from "@awc/XDL/Utilities";
import { LazyImageStyle } from "@insite/mobius/LazyImage";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

headerStyles.page = {
    fullWidth: [true, true, true, true, true],
    padding: 0,
    css: css`
        z-index: ${getProp("theme.zIndex.header")};

        ${LoadingSpinnerStyle} {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            z-index: 999999;
        }
    `,
};

headerStyles.container = {
    css: css`
        display: flex;
        flex-direction: column;
        position: relative;
        min-height: 178px;

        ${xScreen("lte", 1334)} {
            min-height: 228px;
        }
        ${xScreen("lte", "sm")} {
            flex-direction: row;
            align-items: center;
            flex-wrap: wrap;
            gap: 12px;
            min-height: 128px;
        }
    `,
};

headerStyles.topSection = {
    css: css`
        background: ${color("common.ui2")};
        border-bottom: 1px solid ${color("common.ui3")};

        ${(isInShell: any) => {
            if (!isInShell) {
                return css`
                    height: 40px;
                `;
            }
        }}

        ${xScreen("lte", "767")} {
            display: none;
        }
    `,
};

headerStyles.topSectionLanguageCurrencyWrapper = {
    css: css`
        display: flex;
        max-width: fit-content;
        ${xScreen("lte", "md")} {
            flex-wrap: wrap;
        }
    `,
};

headerStyles.languageAndCurrencyWrapper = {
    css: css`
        display: flex;
        ${DivWrapper} {
            display: flex;
            padding: 0 12px;
            &:empty {
                display: none;
            }
            &:first-child {
                ${({ moreThanOneLanguage }: any) =>
                    moreThanOneLanguage &&
                    css`
                        border-right: 1px solid ${color("common.border")};
                    `}
            }
            &:nth-child(2) {
                ${({ moreThanOneCurrency }: any) =>
                    moreThanOneCurrency &&
                    css`
                        border-right: 1px solid ${color("common.border")};
                    `}
            }
        }
    `,
};

headerStyles.middleSection = {
    css: css`
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-block: 12px;
        gap: 54px;
        ${xScreen("gt", "lg")} {
            ${layoutContainer}
        }
        ${xScreen("lte", "md")} {
            padding-left: 16px;
        }
        ${xScreen("lte", "sm")} {
            gap: 16px;
            padding: 0;
            align-items: flex-start;
            padding-left: 8px;
            transition: padding-top 250ms ease-in-out;
            &.has-padding {
                padding-top: 64px;
            }
        }
        ${xScreen("lte", HEADER_BREAKPOINT)} {
            gap: 4px;
        }
    `,
};
headerStyles.logo = {
    css: css`
        max-width: 232px;
        min-height: 68px;

        ${xScreen("lte", "sm")} {
            margin-top: 12px;
        }
        align-self: center;
        &,
        .home-page-link,
        .home-page-link span {
            width: 232px;
            height: 68px;
            aspect-ratio: 232/68;

            ${xScreen("lte", "sm")} {
                width: 164px;
                height: 48px;
                min-height: 48px;
            }
            ${xScreen("lte", HEADER_BREAKPOINT)} {
                width: 145px;
            }
            &:focus-visible {
                outline: 1px solid ${color("primary")};
            }
        }
        ${LazyImageStyle} {
            align-items: flex-start;
            aspect-ratio: 232/68;
        }
    `,
};

headerStyles.search = {
    css: css`
        width: 615px;
        max-width: 615px;
        flex-grow: 1;

        ${xScreen("lte", "md")} {
            width: auto;
        }

        ${xScreen("lte", "sm")} {
            margin-left: auto;
            width: fit-content;
            width: 44px;
            height: 40px;
            background: ${color("common.ui2")};
            border-bottom: 1px solid ${color("common.ui3")};
            display: flex;
            justify-content: center;
            align-items: center;
            &&&.mobile-search-opened {
                background: ${color("primary.main")};
            }
        }
    `,
};

headerStyles.actions = {
    css: css`
        align-self: center;
        justify-self: end;
        > ${DivWrapper} {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            column-gap: 32px;
        }
        ${xScreen("lte", "sm")} {
            display: none;
        }
    `,
};

headerStyles.mainNavigation = {
    css: css`
        background-color: ${color("primary.main")};
        ${xScreen("lte", "sm")} {
            width: auto;
            background-color: ${color("common.ui1")};
            display: flex;
            justify-content: space-between;
            padding-left: 8px;
        }
    `,
};
headerStyles.middleRightSection = {
    css: css`
        display: flex;
        flex-direction: column;
        row-gap: 12px;
        align-items: flex-end;
        flex-grow: 1;
    `,
};

headerStyles.searchAndCart = {
    css: css`
        display: flex;
        column-gap: 20px;
        justify-content: flex-end;
        width: 100%;
        ${xScreen("lte", "sm")} {
            column-gap: 0;
            width: auto;
        }
    `,
};
headerStyles.headerLinks = {
    css: css`
        margin-right: 46px;
        ${xScreen("lte", "md")} {
            margin-right: 16px;
        }
        ${xScreen("lte", "sm")} {
            display: none;
        }
    `,
};
headerStyles.cartlinkWrapper = {
    css: css`
        width: auto;
        margin-left: auto;
        align-self: end;
        ${xScreen("lte", "sm")} {
            padding-left: 8px;
        }
    `,
};

headerStyles.printLogo = companyLogoItem;

headerStyles.printLogoImage = {
    css: css`
        max-width: 232px;
        max-height: 68px;
    `,
};
